import React, { useCallback } from 'react';
import styled from 'styled-components';

import { BOARD_FRIDAY_UNLOCK_LABEL } from '../board/boardProgressCopy';
import { useFreshFindFridaysGate } from './freshFindFridaysFlag';
import { FridayUnlock } from './FridayUnlockPill';
import {
  FRESH_FIND_UNLOCK_ENTRY,
  noteFreshFindUnlockClick,
} from './freshFindUnlockFunnel';

/**
 * Fresh Find Fridays — a FLAT NOTE TAG (THE-873, Kate), not a card.
 *
 * NOT A BUTTON (THE-873). The THE-795 banner shipped as a floating white card
 * with shadow, a circular badge and a bold title — which Kate's screenshot
 * called out as reading like an action, when the surface is pure information.
 * `Tag` below is therefore cream-filled, flat, has no shadow/border/hover and
 * is a plain `<div>` — not focusable, not a button or a link. The star lost
 * its ring and circle with it: `✦` is now just a coloured character inline in
 * the copy, the way the mock sets it, not a drawn badge.
 *
 * DESKTOP STAYS BESIDE THE HEADING; PHONE GOES FULL-WIDTH BELOW IT
 * (THE-873 follow-up, Kate 2026-10-07). The first pass shared the heading's
 * row at every width; Kate's second look moved the phone tag to its own
 * full-width line under "Your people · 34" (count restored) so the signed-out
 * "Sign in to unlock" line has room. `HeadingWrap` (BrowseMyFriendsSection.tsx)
 * now switches from a stacked column to a row only at 768px — the same
 * breakpoint `Tag`'s own copy swap below uses, so there is still only one
 * cutoff for the whole row. `TagLineDesktop` holds the long one-line copy and
 * is hidden below 768px; `TagLinePhoneTitle` + `TagLinePhoneSubtext` hold the
 * two-line copy and are hidden at or above it. CSS-only, not a window-width
 * read, for the same jsdom-can't-see-`@media` reason the style-contract test
 * documents.
 *
 * TWO VARIANTS, ONE TAG, AND THE SIGN-IN DOOR LIVES INSIDE IT (Kate
 * 2026-10-07, reversing the first pass). A viewer with no account gets the
 * tag's copy ending in the sign-in door rather than the signed-in closer — not
 * a separate door beside the tag. The tag element itself stays non-interactive
 * (flat, no hover, normal cursor, not focusable); only the nested button is
 * tappable, and it carries its own invisible 44px-tall hit area (`::after`)
 * since the visible text is smaller than that.
 *
 * THE DOOR IS NOW THE SHARED "🔒 Sign in" PILL, NOT A LOCAL UNDERLINED LINK
 * (THE-970). It is `FridayUnlock` from `./FridayUnlockPill` — the same
 * component the board's Friday band and stage-4 card render — so the
 * affordance, the tap target and the funnel entry are one implementation
 * across every Friday surface. Only the accessible name stays this surface's
 * own (`FRESH_FIND_SIGNIN_ARIA_LABEL`); the feature's name and the copy around
 * the pill are unchanged.
 * The variant is chosen by the SLOT at the bottom of this file, from a
 * `hasAccount` answer the host supplies; nothing here reads auth.
 *
 * MOUNTED VIA `FreshFindFridaysCardSlot`, NEVER DIRECTLY. The slot is the only
 * thing that touches the feature flag, and it renders nothing on BOTH
 * `pending` and `disabled` — the three-state contract from
 * freshFindFridaysFlag.ts. The tag below is flag-blind on purpose so stories
 * and tests can render it without a Firestore stub.
 *
 * WHERE IT SITS. BrowseMyFriendsSection's `HeadingWrap` — the "Your people"
 * line — hosts the slot, and that wrapper (not this file) owns the row's
 * alignment and the column/row switch, because that is a property of the row
 * the two things share and cannot be expressed from inside one of them.
 * Pinned by FreshFindFridaysCardStyleContract.test.ts.
 *
 * COPY GROUND RULE (THE-769): the word "shop" never appears on this surface.
 */

export const FRESH_FIND_TITLE = 'Fresh Find Fridays';
/** Desktop (>=768px) signed-in copy, and the phone signed-in second line —
 *  verbatim from the mock. */
export const FRESH_FIND_SUBTEXT_DESKTOP = 'New picks weekly. Gift genius, on standby.';
/** The lead-in before "Sign in to unlock" (desktop, signed out) and the
 *  phone signed-out second line — verbatim from the mock. */
export const FRESH_FIND_SUBTEXT_PHONE = 'New picks weekly.';
/**
 * Visible pill copy — RE-EXPORTED, NOT RE-DECLARED (THE-970).
 *
 * Through THE-873 this was its own string, `'Sign in to unlock'`, rendered as
 * an underlined text link. THE-970 adopts the shared "🔒 Sign in" pill here,
 * and the whole value of a shared affordance is that the two cannot drift —
 * so this alias points at the pill's own constant rather than restating its
 * text. The lead-in copy around it (`FRESH_FIND_SUBTEXT_*`) and the feature's
 * name are untouched: THE-970 is a control-style change, not a copy change.
 */
export const FRESH_FIND_SIGNIN_LABEL = BOARD_FRIDAY_UNLOCK_LABEL;
/**
 * The accessible name is UNCHANGED and deliberately longer than the visible
 * label: "Sign in" read out of context by a screen reader doesn't say what it
 * signs you in to. THE-970's rule for both adopting surfaces is "keep each
 * surface's existing full accessible name", so the pill takes this verbatim
 * via its `ariaLabel` prop instead of the board's Friday sentence.
 */
export const FRESH_FIND_SIGNIN_ARIA_LABEL = 'Sign in to unlock Fresh Find Fridays';

/** Below this, the tag shows the two-line phone copy; at or above it, the
 *  one-line desktop copy. Same cutoff `Heading`'s own font-size step and the
 *  people-count already use, so the tag doesn't introduce a third breakpoint
 *  for the same row.
 *
 *  Written as the literal `768px` in each `@media` below, not interpolated
 *  from a constant: the style-contract test reads this file as source TEXT
 *  (jsdom ignores `@media` entirely), so an interpolated `${CONST}px` would
 *  leave literal, un-evaluated `${...}` in the string the test scans — the
 *  same reasoning FreshFindFridaysCardStyleContract.test.ts's own docblock
 *  gives for reading CSS as text in the first place. */

// FULL WIDTH BELOW 768px (own line under the heading), SHRINK-WRAPPED AND
// INLINE BESIDE THE HEADING AT 768px UP — the same cutoff the copy swap
// below uses, so the tag only ever has one personality per width.
const Tag = styled.div`
  box-sizing: border-box;
  display: block;
  width: 100%;
  background: ${({ theme }) => theme.color.cream};
  border-radius: 8px;
  padding: 6px 11px;
  /* No shadow, no border, no hover state — it is information, not a button. */
  cursor: default;
  text-align: left;
  font-family: ${({ theme }) => theme.font.sans};
  @media (min-width: 768px) {
    display: inline-block;
    width: auto;
    flex: 0 0 auto;
  }
`;

/** The clay `✦`, inline in the copy — no circle, no ring. */
const Star = styled.span`
  color: ${({ theme }) => theme.color.clay};
`;

const TitleText = styled.span`
  font-weight: 600;
  color: hsl(var(--foreground));
`;

const TagLineDesktop = styled.div`
  display: none;
  white-space: nowrap;
  font-size: 13px;
  line-height: 1.4;
  color: hsl(var(--muted-foreground));
  @media (min-width: 768px) {
    display: block;
  }
`;

// Line 1 on phone: the name, and — signed out only — the sign-in link at the
// far right (`justify-content: space-between`). Signed in, there is only one
// child, so the row collapses to the same look as a plain inline line.
const TagLinePhoneTitle = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
  color: hsl(var(--foreground));
  @media (min-width: 768px) {
    display: none;
  }
`;

const TagLinePhoneSubtext = styled.div`
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.3;
  color: hsl(var(--muted-foreground));
  @media (min-width: 768px) {
    display: none;
  }
`;

// ─── The no-account sign-in door, now INSIDE the tag (THE-873 follow-up,
// Kate 2026-10-07, reversing the first pass's "own quiet link beside the
// tag"), AND NOW THE SHARED PILL RATHER THAN A LOCAL LINK (THE-970) ──────
//
// What used to live here was a `styled.button` of its own: dark ink, underlined,
// font-size inherited from the tag's copy, with a 44px `::after` hit area. It is
// gone, not refactored. `FridayUnlock` draws the identical affordance the board
// draws — same radius, same weight, same 44px invisible tap target — so keeping
// a second declaration here would be keeping the drift THE-968 call 3 extracted
// the component to prevent.
//
// `onDark={false}` because `Tag` is `theme.color.cream`: that is exactly the
// pale-wash case the pill's near-black-on-cream variant exists for, and the
// white variant would vanish into it. `size` stays the default `sm` — this pill
// sits inline in 13px/15px tag copy, not as stage 4's big standalone button.

export interface FreshFindFridaysCardProps {
  /**
   * The no-account variant: supply a handler and the tag's copy ends in a
   * "Sign in to unlock" link instead of the signed-in closer; omit it and
   * this is just the signed-in tag.
   *
   * ONE PROP, NOT TWO. A `showUnlock` boolean beside an `onUnlock` handler can
   * be set to disagree — a link that opens nothing, which `signInAlwaysReachable`
   * exists to call what it is: a decoration, not a door. Here the handler IS
   * the condition, so the disagreement cannot be expressed.
   */
  onUnlock?: () => void;
}

/** `data-signin-entry` is the repo-wide marker for a sign-in door
 *  (src/__tests__/signInAlwaysReachable.test.tsx reads it), and it carries
 *  the same verbatim string this door passes to requestSignIn — so the
 *  attribute and the analytics can't drift. Rendered twice (desktop line +
 *  phone line) because the two live inside CSS-exclusive copy variants, the
 *  same reason the title/star text already appears twice above; only one is
 *  ever in the accessibility tree at a given width, since its sibling is
 *  `display: none`. */
function SignIn({ onUnlock }: { onUnlock: () => void }) {
  return (
    <FridayUnlock
      onUnlock={onUnlock}
      onDark={false}
      entry={FRESH_FIND_UNLOCK_ENTRY}
      ariaLabel={FRESH_FIND_SIGNIN_ARIA_LABEL}
      testId="fresh-find-unlock"
    />
  );
}

export const FreshFindFridaysCard: React.FC<FreshFindFridaysCardProps> = ({
  onUnlock,
}) => (
  <Tag
    role="note"
    // The whole tag is text; the accessible name is just the surface's
    // name, with both copy lines read as the note's content.
    aria-label={FRESH_FIND_TITLE}
    data-testid="fresh-find-fridays-card"
  >
    <TagLineDesktop>
      <Star aria-hidden="true">✦</Star> <TitleText>{FRESH_FIND_TITLE}</TitleText>
      {' · '}
      {onUnlock ? (
        <>
          {FRESH_FIND_SUBTEXT_PHONE} <SignIn onUnlock={onUnlock} />
        </>
      ) : (
        FRESH_FIND_SUBTEXT_DESKTOP
      )}
    </TagLineDesktop>
    <TagLinePhoneTitle>
      <span>
        <Star aria-hidden="true">✦</Star> <TitleText>{FRESH_FIND_TITLE}</TitleText>
      </span>
      {onUnlock && <SignIn onUnlock={onUnlock} />}
    </TagLinePhoneTitle>
    <TagLinePhoneSubtext>
      {onUnlock ? FRESH_FIND_SUBTEXT_PHONE : FRESH_FIND_SUBTEXT_DESKTOP}
    </TagLinePhoneSubtext>
  </Tag>
);

export interface FreshFindFridaysCardSlotProps {
  /**
   * Does this viewer have a permanent account? REQUIRED, and deliberately not
   * defaulted: it decides whether a sign-in door appears, and a default would
   * let a host mount this surface without ever deciding. The HOST answers it
   * because the host is what subscribes to auth — this component stays
   * auth-blind, the same way it is flag-blind below the slot.
   *
   * Anonymous visitors are `false`. They are the Unlock cohort: they have a
   * uid and boards, but no account to deliver a Friday drop to.
   */
  hasAccount: boolean;
  /**
   * Opens the sign-in modal for the named entry — the homepage's existing
   * door signature, the one `homepage-header` / `homepage-boards-row` /
   * `homepage-sticky` already use. App.js turns it into
   * `requestSignIn({ mode: 'signin', entry })`.
   *
   * INJECTED RATHER THAN READ FROM `AuthGateContext` HERE, and that is not
   * taste: importing the gate module pulls Firebase Functions initialisation
   * in at import time (`firebaseFunctions.ts` calls `getFunctions(...)` at
   * module scope), which is the same weight `authConfirmation.tsx` documents
   * going out of its way to keep off this path. A tag on the People grid
   * should not drag the auth SDK behind it.
   *
   * Absent → no link. A door that cannot open is a decoration, and
   * `signInAlwaysReachable` is explicit that those do not count.
   */
  onSignInClick?: (entry: string) => void;
}

/**
 * The flag-gated mount point. This is what a page imports.
 *
 * Renders nothing on `pending` AND nothing on `disabled` — the THE-664 defect
 * the flag file's header describes is exactly this component rendering the
 * "Your people" line twice, once without the tag and once with it, on the
 * page Kate looks at most. Treating `pending` as "not yet" rather than as
 * "off" is what keeps that single render honest.
 *
 * It is also where the Unlock door is WIRED (PR 4.5 / THE-770). The link opens
 * sign-in with the entry `fresh_find_fridays` and does nothing else: there is
 * no in-app-browser special case, because `AuthGate` renders `V4SignInModal`
 * for every viewer and that modal's webview branch is email + password, not
 * OTP (the board's correction to plan item 21 — `useOtpSignIn` lives only in
 * the legacy `SignInModal`, which production never renders). It reaches
 * `requestSignIn` through the homepage's existing `onSignInClick(entry)`
 * opener, which App.js has already bound to
 * `requestSignIn({ mode: 'signin', entry })` — `signin` being the mode
 * `requestSignIn` defaults to for an options object that names none, so this
 * is the issue's `requestSignIn({ entry: 'fresh_find_fridays' })` arriving
 * through the door every other homepage CTA uses.
 */
export const FreshFindFridaysCardSlot: React.FC<FreshFindFridaysCardSlotProps> = ({
  hasAccount,
  onSignInClick,
}) => {
  const gate = useFreshFindFridaysGate();
  // Fire the GA4 click + arm the funnel BEFORE opening the door: the webview
  // email+password path can complete the whole auth without a navigation, so
  // the arm has to be in storage before the modal can report against it.
  const handleUnlock = useCallback(() => {
    noteFreshFindUnlockClick();
    onSignInClick?.(FRESH_FIND_UNLOCK_ENTRY);
  }, [onSignInClick]);

  if (gate !== 'enabled') return null;
  return (
    <FreshFindFridaysCard
      onUnlock={hasAccount || !onSignInClick ? undefined : handleUnlock}
    />
  );
};

export default FreshFindFridaysCard;
